import { lazy, Suspense, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Header from "@/components/Header";
import FixedBackground from "@/components/FixedBackground";
import ScrollCinema from "@/components/cinematic/ScrollCinema";
import FloatingCTA from "@/components/cinematic/FloatingCTA";
import RevealSection from "@/components/cinematic/RevealSection";
import { useLenisScroll } from "@/hooks/useLenisScroll";
import { scrollToAnchor } from "@/lib/smoothScroll";
import { useLanguage } from "@/i18n/LanguageContext";
import { homeSeo } from "@/i18n/seo";
import { t } from "@/i18n/translations";
import { LANG_HTML, LOCALE, SITE_URL, localizedPath } from "@/lib/routes";

const FeaturesSection = lazy(() => import("@/components/FeaturesSection"));
const HowItWorksSection = lazy(() => import("@/components/HowItWorksSection"));
const ProductTour = lazy(() => import("@/components/ProductTour"));
const PricingSection = lazy(() => import("@/components/PricingSection"));
const ContactSection = lazy(() => import("@/components/ContactSection"));
const CTASection = lazy(() => import("@/components/CTASection"));
const Footer = lazy(() => import("@/components/Footer"));

const Index = () => {
  useLenisScroll(true);
  const location = useLocation();
  const { lang } = useLanguage();
  const canonical = `${SITE_URL}${localizedPath("home", lang)}`;
  const title = t(homeSeo.title, lang);
  const description = t(homeSeo.description, lang);

  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash;
    let cancelled = false;
    let attempts = 0;
    const scrollLoop = () => {
      if (cancelled) return;
      const el = document.querySelector(id) as HTMLElement | null;
      if (el) {
        scrollToAnchor(id);
        // Re-scroll a few times as lazy sections load and shift layout.
        if (attempts++ < 8) setTimeout(scrollLoop, 400);
        return;
      }
      if (attempts++ < 40) setTimeout(scrollLoop, 150);
    };
    const t = setTimeout(scrollLoop, 200);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [location.hash]);


  return (
    <div className="dark relative min-h-screen bg-background text-foreground">
      <Helmet>
        <html lang={LANG_HTML[lang]} />
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />
        <link rel="alternate" hrefLang="pt-BR" href={`${SITE_URL}${localizedPath("home", "pt")}`} />
        <link rel="alternate" hrefLang="en" href={`${SITE_URL}${localizedPath("home", "en")}`} />
        <link rel="alternate" hrefLang="es" href={`${SITE_URL}${localizedPath("home", "es")}`} />
        <link rel="alternate" hrefLang="x-default" href={`${SITE_URL}${localizedPath("home", "pt")}`} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content={LOCALE[lang]} />
        <meta property="og:site_name" content="SentinelHub" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
      </Helmet>
      <Header />
      <main>
        <ScrollCinema />
        <div className="relative">
          <FixedBackground />
          <div className="pointer-events-none absolute inset-x-0 top-0 z-10 h-32 bg-gradient-to-b from-[hsl(222,47%,6%)] to-transparent" />
          <div className="relative z-10">
            <Suspense fallback={null}>
              <RevealSection><FeaturesSection /></RevealSection>
              <RevealSection><HowItWorksSection /></RevealSection>
              <RevealSection><ProductTour /></RevealSection>
              <RevealSection><PricingSection /></RevealSection>
              <RevealSection><ContactSection /></RevealSection>
              <RevealSection><CTASection /></RevealSection>
            </Suspense>
          </div>
        </div>
      </main>
      <div className="relative z-10">
        <Suspense fallback={null}>
          <Footer />
        </Suspense>
      </div>
      <FloatingCTA />
    </div>
  );
};

export default Index;
